import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Item,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system';

import { AlertTriangle, XCircle, SearchX, Search, X } from 'lucide-react';

import { useState } from 'react';
import { useLinkExistingPartner } from '@/features/add-partner';

import { fullName } from '@shared';

interface LinkExistingPartnerDialogProps {
  open: boolean;
  client: Client;
  onClose: () => void;
  onSuccess?: () => void;
}

export function LinkExistingPartnerDialog({
  open,
  client,
  onClose,
  onSuccess,
}: LinkExistingPartnerDialogProps) {
  const handleSuccess = () => {
    onClose();
    onSuccess?.();
  };

  const {
    selectedClient,
    setSelectedClient,
    isInitialClientDifferentFromSelectedClient,
    isAlreadyPartneredError,
    enableSave,
    isLastNameMismatch,
    title,
    filterClients,
    save,
    isPending,
  } = useLinkExistingPartner({ client, open, onSuccess: handleSuccess });

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="max-w-[calc(100%-2rem)] sm:max-w-lg">
        <DialogHeader className="text-start">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {!selectedClient && (
          <div className="flex w-full min-w-0 items-start">
            <ClientPartnerSearchBar
              client={client}
              filterClients={filterClients}
              handleSelectClient={setSelectedClient}
            />
          </div>
        )}

        {selectedClient && (
          <>
            <h2 className="text-sm text-gray-950">Selected partner</h2>
            <Item
              variant="muted"
              className="bg-muted w-full min-w-0 flex-nowrap gap-3 p-2"
            >
              <ItemMedia
                variant="icon"
                className="size-10"
              >
                {selectedClient.clientFirstName.charAt(0) +
                  selectedClient.clientLastName.charAt(0)}
              </ItemMedia>
              <ItemContent className="min-w-0 gap-0">
                <ItemTitle>{fullName(selectedClient)}</ItemTitle>
                {selectedClient.clientEmail && (
                  <ItemDescription className="truncate">
                    {selectedClient.clientEmail}
                  </ItemDescription>
                )}
              </ItemContent>
              <Button
                variant="ghost"
                size="icon-sm"
                data-testid="remove-linked-client-button"
                onClick={() => setSelectedClient(null)}
                className="hover:bg-destructive/5 text-foreground hover:text-destructive mr-0.5 rounded-sm"
              >
                <X />
              </Button>
            </Item>

            {isLastNameMismatch &&
              isInitialClientDifferentFromSelectedClient &&
              !isAlreadyPartneredError && <LastNameMismatchWarning />}

            {isAlreadyPartneredError && <AlreadyPartneredError selectedClient={selectedClient} />}
          </>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          {enableSave ? (
            <Button
              onClick={save}
              disabled={isPending}
              className="w-full sm:w-auto"
            >
              {isPending ? 'Saving...' : 'Save'}
            </Button>
          ) : (
            <Tooltip>
              <TooltipTrigger>
                <Button
                  disabled
                  className="pointer-events-none w-full sm:w-auto"
                >
                  Save
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {isAlreadyPartneredError ? 'Fix errors' : 'Select a partner'}
              </TooltipContent>
            </Tooltip>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ClientPartnerSearchBarProps {
  handleSelectClient: (client: Client) => void;
  filterClients: (query: string) => Client[];
  client: Client;
}

function ClientPartnerSearchBar({
  handleSelectClient,
  filterClients,
}: ClientPartnerSearchBarProps) {
  const [query, setQuery] = useState('');

  const filteredClients = filterClients(query);

  const showResults = query.length > 0;

  function handleSelect(client: Client) {
    handleSelectClient(client);
    setQuery('');
  }

  return (
    <div className="w-full min-w-0">
      <div className="relative">
        <Search className="text-muted-foreground pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2" />
        <Input
          autoFocus
          className="pl-9"
          placeholder="Search for partner by name..."
          value={query}
          onChange={event => setQuery(event.target.value)}
        />
      </div>

      {showResults && (
        <Card className="mt-2 rounded-lg p-1 shadow-none">
          {filteredClients.length > 0 ? (
            <ItemGroup className="max-h-64 w-full overflow-y-auto">
              {filteredClients.map(client => (
                <Item
                  role="button"
                  onMouseDown={() => handleSelect(client)}
                  className="hover:bg-muted text-foreground w-full min-w-0 flex-nowrap items-center gap-3 rounded-sm p-2 transition-colors hover:text-gray-950"
                >
                  <ItemMedia
                    variant="icon"
                    className="size-10"
                  >
                    {client.clientFirstName.charAt(0) + client.clientLastName.charAt(0)}
                  </ItemMedia>
                  <ItemContent className="min-w-0 gap-0">
                    <ItemTitle>{fullName(client)}</ItemTitle>
                    {client.clientEmail && (
                      <ItemDescription className="truncate">{client.clientEmail}</ItemDescription>
                    )}
                  </ItemContent>
                </Item>
              ))}
            </ItemGroup>
          ) : (
            <div className="text-foreground flex flex-col items-center gap-2 p-6 text-center text-sm">
              <SearchX className="size-5" />
              <p className="font-medium text-gray-950">No results found</p>
              <p>No clients found for this search term. Please try again.</p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function LastNameMismatchWarning() {
  return (
    <Alert variant="warning">
      <AlertTriangle className="size-4" />
      <AlertTitle>Last name mismatch</AlertTitle>
      <AlertDescription>
        The last names of the clients you are linking do not match. Please confirm that you are
        linking the correct clients.
      </AlertDescription>
    </Alert>
  );
}

interface AlreadyPartneredErrorProps {
  selectedClient: Client;
}

function AlreadyPartneredError({ selectedClient }: AlreadyPartneredErrorProps) {
  return (
    <Alert variant="destructive">
      <XCircle className="size-4" />
      <AlertTitle>Already Linked Error</AlertTitle>
      <AlertDescription>
        {fullName(selectedClient)} is already linked to another client. Please unlink their current
        partner in order to perform this action.
      </AlertDescription>
    </Alert>
  );
}
